"use client";

import * as React from "react";
import { Box, Card, Flex, Text, Grid, Link, Code } from "@radix-ui/themes";
import { OrganizationDomain } from "../api/endpoint.js";
import { NameServerIcon } from "./name-server-icon.js";
import { IconPanel } from "./icon-panel.js";
import { CopyIconButton } from "./copy-button.js";
import { Dialog, Button } from "./elements.js";
import { Translation } from "./i18n/translation.js";

interface ViewDnsRecordDialogProps {
  children?: React.ReactNode;
  domain: OrganizationDomain;
  onReverifyDomain: (domainId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ViewDnsRecordDialog = (props: ViewDnsRecordDialogProps) => {
  const isFailed = props.domain.state === "Failed";

  const handleRestartVerification = () => {
    props.onReverifyDomain(props.domain.id);
    props.onOpenChange(false);
  };

  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      {props.children && <Dialog.Trigger>{props.children}</Dialog.Trigger>}
      <Dialog.Content style={{ maxWidth: "530px" }}>
        <Box mb="5">
          <Dialog.Title size="5" mb="2">
            {isFailed ? (
              <Translation
                defaultMessage="Restart verification"
                id="k9X2ru"
                description="Dialog title for restarting domain verification"
              />
            ) : (
              <Translation
                defaultMessage="View DNS record"
                id="/aCgMQ"
                description="Dialog title for viewing DNS record"
              />
            )}
          </Dialog.Title>
          <Text size="2" highContrast>
            {isFailed ? (
              <Translation
                defaultMessage="The domain {domain} will be automatically verified once the following DNS record is detected. Use a tool like {dnsChecker} to check the domain's record for any mistakes."
                id="yk7ScH"
                description="Instructions for failed domain verification with DNS checker tool"
                values={{
                  domain: <b>{props.domain.domain}</b>,
                  dnsChecker: (
                    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                    <Link href="https://dnschecker.org/">DNSChecker</Link>
                  ),
                }}
              />
            ) : (
              <Translation
                defaultMessage="The domain {domain} will be automatically verified once the following DNS record is detected."
                id="7+lT5Q"
                description="Instructions for domain verification"
                values={{
                  domain: <b>{props.domain.domain}</b>,
                }}
              />
            )}
          </Text>
        </Box>

        <DnsRecordCard domain={props.domain} />
        <Flex justify="end" gap="2" mt="5">
          {isFailed ? (
            <>
              <Dialog.Close>
                <Button variant="secondary">
                  <Translation
                    defaultMessage="Close"
                    id="ESS/aT"
                    description="Button text to close dialog"
                  />
                </Button>
              </Dialog.Close>
              <Button onClick={handleRestartVerification}>
                <Translation
                  defaultMessage="Restart verification"
                  id="eSQSjC"
                  description="Button text to restart domain verification"
                />
              </Button>
            </>
          ) : (
            <Dialog.Close>
              <Button>
                <Translation
                  defaultMessage="Close"
                  id="ESS/aT"
                  description="Button text to close dialog"
                />
              </Button>
            </Dialog.Close>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const DnsRecordCard = (props: { domain: OrganizationDomain }) => {
  const verificationContent = `${props.domain.verificationPrefix ?? "verification_token"}=${props.domain.verificationToken}`;
  return (
    <Card size="2">
      <Flex gap="2" align="center" mb="4">
        <IconPanel
          color={props.domain.nameServer === "Other" ? "gray" : "panel"}
        >
          <NameServerIcon provider={props.domain.nameServer} />
        </IconPanel>

        <Text size="2" highContrast weight="bold" as="p" mb="-2px">
          {props.domain.domain}
        </Text>
      </Flex>
      <Grid
        gap="4"
        style={{
          alignItems: "start",
          gridTemplateColumns: "146px 1fr",
        }}
      >
        <Text color="gray" weight="medium" size="2">
          <Translation
            defaultMessage="Type"
            id="/UCweF"
            description="Label for DNS record type field"
          />
        </Text>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <Text size="2">TXT</Text>

        <Text color="gray" weight="medium" size="2">
          <Translation
            defaultMessage="Host / Name"
            id="dx3Cso"
            description="Label for DNS host/name field"
          />
        </Text>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <Text size="2">{props.domain.subdomain || "@"}</Text>

        <Text color="gray" weight="medium" size="2">
          <Translation
            defaultMessage="Value"
            id="MaYH9g"
            description="Label for DNS record value field"
          />
        </Text>
        <Flex gap="2" align="start">
          <Code size="2" variant="ghost">
            {verificationContent}
          </Code>
          <CopyIconButton value={verificationContent} />
        </Flex>
      </Grid>
    </Card>
  );
};
